import { type HTMLAttributes, forwardRef } from 'react';

import styles from './NotebookSheet.module.css';

/**
 * 좌측 마진 라인의 색. 페이지의 "주제" 컬러를 결정한다.
 *  - red    : 입력/액션 페이지 (코드 입력 등 주의·집중)
 *  - mint   : 완료/축하 페이지 (다운로드 등)
 *  - blue   : 정보/참고 페이지
 *  - 'none' : 마진 라인 없음 (이미지·미디어가 페이지 전체를 채울 때)
 */
type MarginColor = 'red' | 'blue' | 'yellow' | 'mint' | 'pink' | 'none';

export interface NotebookSheetProps extends HTMLAttributes<HTMLDivElement> {
  /** 좌측 세로 마진 라인의 색. 기본 빨강. `flat`이 true면 자동으로 `none`으로 처리된다. */
  marginColor?: MarginColor;
  /** 가로 노트선(연한 파란선) 표시 여부. 기본 true. */
  ruled?: boolean;
  /**
   * 미디어 콘텐츠(이미지·일러스트)를 위해 좌측 마진/노트선을 자동 비활성화하고
   * 본문 패딩을 줄여 페이지 전체를 미디어로 채울 수 있게 한다.
   */
  flat?: boolean;
}

/**
 * "노트 한 페이지" 컴포넌트.
 *
 * 시각 구성:
 *  - 좌측: 빨간/민트/파랑 등 컬러 세로 마진 라인 (`marginColor`)
 *  - 본문: 옅은 파란색 가로 노트선 (`ruled`)
 *  - 외곽: 사방 2px 잉크 보더 + 어긋난 잉크 그림자
 *  - 배경: 크림 페이퍼 + 종이 그레인 텍스처
 *
 * 디자인 시스템 §종이·크레파스·노트 컨셉을 단일 컴포넌트로 캡슐화해
 * 페이지 전반에 일관된 미학을 보장한다.
 *
 * @see PaperCard - 짧은 정보 카드용 (보더만 있는 단순 흰 카드)
 */
export const NotebookSheet = forwardRef<HTMLDivElement, NotebookSheetProps>(
  function NotebookSheet(
    { marginColor = 'red', ruled = true, flat = false, className, children, ...rest },
    ref,
  ) {
    const effectiveMargin: MarginColor = flat ? 'none' : marginColor;
    const effectiveRuled = !flat && ruled;

    const classes = [
      styles.sheet,
      styles[`margin-${effectiveMargin}`],
      effectiveRuled ? styles.ruled : '',
      flat ? styles.flat : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={classes} {...rest}>
        <div className={styles.body}>{children}</div>
      </div>
    );
  },
);
